import React from 'react';
import { useAuthContext } from '@hero/hero-auth/v2/AuthProvider';
import envVars from '../constants/envVars';
import { IUserStatus, OrgAccess } from '../pages/IAM/types';
import useGetUserPermissions, { UserPermissions } from '../hooks/useGetUserPermissions';
import queryClient from '../queryClient';

type IUserStatusContext = {
    isLoading: boolean;
    userStatus?: IUserStatus;
    permissions: UserPermissions;
    orgs: OrgAccess[];
    activeOrgId?: string;
    // Role of the ACTIVE org grant. Prefer this over `userStatus.role`, which is the
    // primary org's role and goes stale the moment the user switches orgs.
    activeRole?: OrgAccess['role'];
    setActiveOrg: (heroOrganizationId: string) => void;
};

const UserStatusContext = React.createContext<IUserStatusContext | undefined>(undefined);

export const useUserStatusContext = () => {
    const ctx = React.useContext(UserStatusContext);

    if (ctx === undefined) {
        throw new Error(`'useUserStatusContext' must be used within a 'UserStatusContextProvider'`);
    }

    return ctx;
};

// Default active org = the primary grant, else the first org, else the additive scalar
// (single-org / pre-orgs payload). Keeps single-org behaviour identical.
const resolveDefaultOrgId = (status?: IUserStatus): string | undefined => {
    if (!status) {
        return undefined;
    }
    const orgs = status.orgs ?? [];
    const primary = orgs.find((o) => o.is_primary);
    return (
        primary?.hero_organization_id ??
        orgs[0]?.hero_organization_id ??
        status.hero_organization_id
    );
};

const UserStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [userStatus, setUserStatus] = React.useState<IUserStatus>();
    const [activeOrgId, setActiveOrgId] = React.useState<string>();
    const [isLoading, setIsLoading] = React.useState(false);
    const { auth, logout } = useAuthContext();

    const orgs = userStatus?.orgs ?? [];
    const activeOrg = orgs.find((o) => o.hero_organization_id === activeOrgId);
    // Permissions and role resolve from the ACTIVE org; fall back to the additive
    // top-level values so a single-org user behaves exactly as before.
    const permissions = useGetUserPermissions(activeOrg?.permissions ?? userStatus?.permissions);
    const activeRole = activeOrg?.role ?? userStatus?.role;

    const setActiveOrg = React.useCallback((heroOrganizationId: string) => {
        setActiveOrgId((current) => {
            if (current === heroOrganizationId) {
                return current;
            }
            // Query keys carry request params but not the org, so a same-params request under
            // the new provider would be served the previous provider's cached rows. Evict, not
            // invalidate — this is cross-tenant data, so stale must never render.
            queryClient.clear();
            return heroOrganizationId;
        });
    }, []);

    React.useEffect(() => {
        if (!auth) {
            return;
        }

        setIsLoading(true);

        fetch(`${envVars.API_ID_HOSTNAME}api/enterprise/user-status`, {
            headers: { Authorization: `Bearer ${auth.access_token}` }
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`${res.status} ${res.statusText}`);
                }

                res.json().then((data: IUserStatus) => {
                    setUserStatus(data);
                    // This effect re-runs on every token refresh (new `auth` identity), so keep
                    // the org the user picked as long as they still hold a grant for it —
                    // re-resolving the default would silently move them back to their primary.
                    setActiveOrgId((current) =>
                        current && (data.orgs ?? []).some((o) => o.hero_organization_id === current)
                            ? current
                            : resolveDefaultOrgId(data)
                    );
                    setIsLoading(false);
                });
            })
            .catch((error) => {
                setIsLoading(false);
                console.log('Error: ', error);
            });
    }, [auth, logout]);

    return (
        <UserStatusContext.Provider
            value={{
                userStatus,
                isLoading,
                permissions,
                orgs,
                activeOrgId,
                activeRole,
                setActiveOrg
            }}
        >
            {children}
        </UserStatusContext.Provider>
    );
};

export default UserStatusProvider;
